import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(duration);
dayjs.extend(relativeTime);
// allows locale formatting
dayjs.extend(LocalizedFormat);
// allows timezone conversion to UTC
dayjs.extend(utc);
// extends with timezone conversion
dayjs.extend(timezone);
// Allows timezones to be formatted in human friendly format
dayjs.extend(advancedFormat);

export const API_DATE_FORMAT = 'YYYY-MM-DD hh:mm:ss';
export const API_MONTH_DAY_ONLY_FORMAT = 'YYYY-MM-DD';
export const MONTH_DAY_YEAR_FORMAT = 'MM/DD/YYYY';
export const MONTH_ABBREVIATION_DAY_YEAR_FORMAT = 'MMM D, YYYY';
export const TIME_WITH_TIMEZONE_FORMAT = 'hh:mm z';
export const DATE_TIME_WITH_TIMEZONE_FORMAT = 'ddd, MMM D [at] h:mm a z';

/**
 * Formats a date using a specified pattern.
 * If no pattern is provided, it defaults to MM/DD/YYYY.
 *
 * @param date - The date to format.
 * @param format - The format to use. Defaults to MM/DD/YYYY.
 * @param fallback - The fallback to display if the date is invalid. Defaults to empty string.
 * @returns The formatted date.
 */
export function formatDate(date: dayjs.ConfigType, format: string = MONTH_DAY_YEAR_FORMAT, fallback = ''): string {
    if (!date) return fallback;
    const dateParsed = dayjs(date);
    if (dateParsed.isValid()) {
        return dateParsed.format(format);
    }
    return fallback;
}

/** Formats a date to a specific timezone, falling back to America/Chicago if none given.*/
export function formatDateToTimezone(
    date: dayjs.ConfigType,
    timezone: string = 'America/Chicago',
    format: string = MONTH_DAY_YEAR_FORMAT,
    fallback = '',
): string {
    if (!date) return fallback;
    const dateParsed = dayjs(date);
    if (dateParsed.isValid()) {
        return dateParsed.tz(timezone).format(format);
    }
    return fallback;
}
/**
 * Returns the relative, human-readable date string for a given date.
 * Ex: '1 day ago', '2 months ago', '3 years ago', etc.
 */
export function getRelativeDate(date: dayjs.ConfigType): string {
    if (!date) return '';

    try {
        const dateParsed = dayjs(date);
        if (dateParsed.isValid()) {
            return dateParsed.fromNow();
        }
        return '';
    } catch (error) {
        return '';
    }
}

/**
 * Converts a UTC date to the user's browser's timezone, returning a dayjs object.
 *
 * @param date - The UTC date to convert.
 * @returns The converted date as a dayjs object.
 */
export function convertUTCDateToLocalTimezone(date: dayjs.ConfigType): dayjs.Dayjs {
    return dayjs.utc(date).local();
}

export const convertHoursToDurationString = (totalHours: number): string | null => {
    if (!totalHours || totalHours <= 0) return null;
    const timeDuration = dayjs.duration(totalHours, 'hours');
    const days = timeDuration.days();
    const hours = timeDuration.hours();
    if (days === 0) return `${hours} hours`;
    if (hours === 0) return `${days} days`;
    return `${days} days, ${hours} hours`;
};

/**
 * Returns the number of whole calendar days that have passed since the given date.
 */
export function daysSince(date: dayjs.ConfigType): number {
    return dayjs().diff(dayjs(date), 'day');
}

export const getNextBusinessDay = ({
    fromDate,
    daysToAdd = 1,
    countWeekendDays = false,
}: { fromDate?: dayjs.ConfigType; daysToAdd?: number; countWeekendDays?: boolean } = {}): Date => {
    let date = fromDate ? dayjs(fromDate) : dayjs();

    const SUNDAY = 0;
    const SATURDAY = 6;

    if (countWeekendDays) {
        date = date.add(daysToAdd, 'day');
    } else {
        let added = 0;
        while (added < daysToAdd) {
            date = date.add(1, 'day');
            if (date.day() !== SUNDAY && date.day() !== SATURDAY) {
                added++;
            }
        }
    }

    // always land on a weekday
    if (date.day() === SATURDAY) return date.add(2, 'day').toDate();
    if (date.day() === SUNDAY) return date.add(1, 'day').toDate();
    return date.toDate();
};
